import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Download, KeyRound } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDate, hasPermission, TENANCY_PARTY_ROLE_LABELS, TENANCY_STATUS_FILTER_LABELS } from '@blimo/shared';
import { Button, DataTable, EmptyState, ErrorState, PageHeader, SearchInput, StatusBadge } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { PartyList, UnitLabel } from '../../components/data.jsx';
import { PropertyFilter } from '../finance/panels.jsx';
import { useListState } from '../../lib/list-search.js';

const STATUS_OPTIONS = Object.entries(TENANCY_STATUS_FILTER_LABELS)
  .filter(([value]) => value !== 'current')
  .map(([value, label]) => ({ value, label }));

/**
 * Verwaltung › Mietverhältnisse (F-702/F-703, read-only – Quelle Buchhaltung/WinCasa). Filter in der URL
 * (`?status=past|all&property=&q=`; ohne `status` = aktuelle wie die API), XLSX-Export mit denselben Filtern.
 */
export function TenanciesListPage() {
  const api = useApi();
  const { data: me } = useMe();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const filter = { status: search.status ?? 'current', property_id: search.property, search: search.q };
  const params = { page: list.apiParams.page, per_page: list.apiParams.per_page, filter };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.tenancies(api, params));
  const columns = useMemo(() => tenancyColumns(), []);

  return (
    <>
      <PageHeader
        title="Mietverhältnisse"
        description="Aktuelle und ehemalige Mietverhältnisse aller Liegenschaften (Quelle: Buchhaltung, nur lesend)."
        actions={
          hasPermission(me, 'tenancies.export') && (
            <Button asChild size="sm" variant="outline">
              <a href={api.admin.tenancies.exportUrl(filter)} download>
                <Download aria-hidden="true" /> Export (XLSX)
              </a>
            </Button>
          )
        }
      />
      <FilterBar>
        <SearchInput label="Mietverhältnisse durchsuchen" placeholder="Name, Konto …" value={search.q ?? ''} onValueChange={(v) => list.setFilter('q', v)} />
        <FilterSelect label="Status" value={search.status} onChange={(v) => list.setFilter('status', v)} options={STATUS_OPTIONS} allLabel="Aktuell" />
        <PropertyFilter value={search.property} onChange={(v) => list.setFilter('property', v)} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Mietverhältnisse"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyState={
            <EmptyState
              compact
              className="border-0"
              icon={KeyRound}
              title="Keine Mietverhältnisse"
              description="Für die gewählten Filter liegen keine Mietverhältnisse vor."
            />
          }
        />
      )}
    </>
  );
}

const date = (key, header) => ({
  id: key,
  header,
  enableSorting: false,
  cell: ({ row }) => <span className="numeric">{formatDate(row.original[key])}</span>,
});

function tenancyColumns() {
  return [
    { id: 'unit', header: 'Einheit', enableSorting: false, cell: ({ row }) => <UnitLabel unit={row.original.unit} /> },
    {
      id: 'account',
      header: 'Konto',
      enableSorting: false,
      cell: ({ row }) => (
        <Link
          to="/mietverhaeltnisse/$tenancyId"
          params={{ tenancyId: String(row.original.id) }}
          className="numeric font-medium text-primary hover:underline"
          aria-label={`Mietverhältnis ${row.original.account_number ?? row.original.id} öffnen`}
        >
          {row.original.account_number ?? `#${row.original.id}`}
        </Link>
      ),
    },
    {
      id: 'tenants',
      header: 'Mieter',
      enableSorting: false,
      cell: ({ row }) => <PartyList parties={row.original.parties} roleLabels={TENANCY_PARTY_ROLE_LABELS} />,
    },
    date('starts_on', 'Beginn'),
    date('ends_on', 'Ende'),
    date('terminated_on', 'Gekündigt am'),
    { id: 'status', header: 'Status', enableSorting: false, cell: ({ row }) => <StatusBadge domain="tenancy" status={row.original.status} /> },
  ];
}
